import type {
  DateRangePickerValue,
  FlexibleCalendarValue,
} from '@lmn/common-search-widget/components/DateRangePicker/services/types';
import { format } from 'date-fns';
import { DateType, DatesType, type Dates, type DateItem } from '@/app/components/search/utils';

const getExactDateItem = (date: Date | null): DateItem => ({
  type: DateType.EXACT,
  date: date ? format(date, 'yyyy-MM-dd') : undefined,
});

const getFlexDateItem = (flexible: FlexibleCalendarValue | undefined): DateItem => {
  if (!flexible?.duration) return { type: DateType.FLEX };
  const [daysFrom, daysTo] = flexible.duration.split('-').map(Number);

  return {
    type: flexible.selectedMonths?.length > 0 ? DateType.MONTHS : DateType.ANYTIME,
    subType: daysFrom === 7 && !daysTo ? DateType.WEEKEND : DateType.TIME,
    daysFrom,
    daysTo: daysTo || daysFrom,
    months: flexible.selectedMonths.map((date: Date) => format(date, 'yyyy-MM')),
  };
};

/**
 * Adapts a `DateRangePickerValue` object from CSW to a `Dates` object used in the application.
 *
 * This function serves as a reverse adapter between the Common Search Widget's `DateRangePickerValue` model
 * and the application's `Dates` model. It converts the input `DateRangePickerValue` object into
 * a format compatible with the application.
 *
 * @param value - A `DateRangePickerValue` object from the Common Search Widget.
 * @returns A `Dates` object compatible with the application.
 *
 * @remarks
 * - The function determines the `type` based on the `mode` property of the input.
 * - It processes both calendar dates (departure and return) and flexible dates.
 * - The `getExactDateItem` helper function is used to create `DateItem` objects for calendar dates.
 * - The `getFlexDateItem` helper function is used to process flexible date information.
 *
 * @example
 * const cswDates = {
 *   mode: 'calendar',
 *   calendar: { startDate: new Date('2023-05-01'), endDate: new Date('2023-05-10') },
 *   flexible: { duration: '7-10', selectedMonths: [] }
 * };
 * const tsxDates = getTSXDates(cswDates);
 * // Returns: { type: DatesType.CALENDAR, departureDate: {...}, returnDate: {...}, flexDate: {...}, subDate: 0 }
 */
const getTSXDates = (dates: DateRangePickerValue): Dates => ({
  type: dates.mode === 'flexible' ? DatesType.FLEXIBLE : DatesType.CALENDAR,
  departureDate: getExactDateItem(dates.calendar.startDate),
  returnDate: getExactDateItem(dates.calendar.endDate),
  flexDate: getFlexDateItem(dates.flexible),
  subDate: 0, // TODO: assuming a default value for subDate?
});

export default getTSXDates;
